import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { authApi, authQueryKeys, useUserInfoQuery } from "@/modules/auth";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";

import { accountConnectUrl, clearConnectIntent, readConnectIntent, type ConnectProvider } from "./connect";

type Status = "loading" | "success" | "invalid" | "failed";

export function AccountConnectContent() {
  const [params] = useSearchParams();
  const userQuery = useUserInfoQuery();
  const user = userQuery.data;
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("loading");
  const started = useRef(false);
  const provider = params.get("provider");
  const code = params.get("code");
  const state = params.get("state");

  useEffect(() => {
    if (userQuery.isError) setStatus("failed");
  }, [userQuery.isError]);

  useEffect(() => {
    if (started.current || !user) return;
    started.current = true;
    const intent = readConnectIntent();
    if ((provider !== "qq" && provider !== "wechat") || !code || !state ||
      !intent || intent.userId !== user.id || intent.provider !== provider) {
      clearConnectIntent();
      setStatus("invalid");
      return;
    }
    const connectProvider = provider as ConnectProvider;
    const request = connectProvider === "qq"
      ? authApi.bindQQ({ code, state, next: accountConnectUrl(connectProvider) })
      : authApi.bindWechat({ code, state });
    void request.then(async () => {
      clearConnectIntent();
      await queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
      setStatus("success");
    }, () => {
      clearConnectIntent();
      setStatus("failed");
    });
  }, [code, provider, queryClient, state, user]);

  const label = provider === "qq" ? "QQ" : "微信";
  const title = status === "loading" ? `正在绑定${label}`
    : status === "success" ? `${label}绑定成功`
      : status === "invalid" ? "授权信息不完整" : `${label}绑定失败`;
  const description = status === "loading" ? "正在确认授权，请稍候。"
    : status === "success" ? "现在可以在账户设置中查看绑定状态。"
      : status === "invalid" ? "请从账户设置重新发起绑定。"
        : "请返回账户设置重试。";

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md" role={status === "failed" || status === "invalid" ? "alert" : "status"}>
        <CardHeader className="justify-items-center text-center">
          {status === "loading" ? <Loader2 className="mb-2 size-9 animate-spin text-muted-foreground" />
            : status === "success" ? <CircleCheck className="mb-2 size-9 text-emerald-600" />
              : <CircleAlert className="mb-2 size-9 text-destructive" />}
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {status !== "loading" && <CardContent className="flex justify-center">
          <Button asChild><Link to={ROUTE_PATHS.MINE}>返回工作台</Link></Button>
        </CardContent>}
      </Card>
    </div>
  );
}
